import { Search, X } from 'lucide-react';
import type { AssigneeDoc, WbsItemStatus } from '@mct/shared';
import { Input } from '../ui/Input';
import { cn } from '../../lib/utils';
import type { DepthMode, WbsFilter } from '../../lib/wbsFilter';

const STATUSES: { value: WbsItemStatus; label: string }[] = [
  { value: 'not_started', label: '未着手' },
  { value: 'in_progress', label: '進行中' },
  { value: 'done', label: '完了' },
  { value: 'blocked', label: 'ブロック' },
  { value: 'cancelled', label: '中止' },
];

const DEPTHS: { value: DepthMode; label: string }[] = [
  { value: 'phase', label: 'フェーズのみ' },
  { value: 'phaseTask', label: 'フェーズ+タスク' },
  { value: 'all', label: '全階層' },
];

export function WbsFilterBar({
  filter,
  assignees,
  onChange,
  onClear,
  hasActive,
}: {
  filter: WbsFilter;
  /**
   * Project-wide assignee list. Same source as the migration-task DAG
   * page (`useAssignees`) so chips and labels stay in lock-step. The
   * filter stores `assigneeId` values; the chip label is the doc's
   * `name`.
   */
  assignees: readonly AssigneeDoc[];
  onChange: (next: WbsFilter) => void;
  onClear: () => void;
  hasActive: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          className="!w-60 pl-7"
          placeholder="名前・ノート検索"
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
        />
      </div>
      <ChipGroup
        label="ステータス"
        options={STATUSES.map((s) => ({ value: s.value, label: s.label }))}
        selected={filter.statuses}
        onToggle={(v) =>
          onChange({
            ...filter,
            statuses: toggle(filter.statuses, v as WbsItemStatus),
          })
        }
      />
      {/* 領域 filter is surfaced separately via <WbsAreaFilter>
         so it stays visible even when this panel is collapsed. */}
      {assignees.length > 0 ? (
        <ChipGroup
          label="担当"
          options={assignees.map((a) => ({
            value: a.assigneeId,
            label: a.name,
          }))}
          selected={filter.assignees}
          onToggle={(v) =>
            onChange({
              ...filter,
              assignees: toggle(filter.assignees, v),
            })
          }
        />
      ) : null}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-slate-700 hover:border-slate-400">
        <input
          type="checkbox"
          className="h-3 w-3"
          checked={filter.delayed}
          onChange={(e) => onChange({ ...filter, delayed: e.target.checked })}
        />
        遅延中のみ
      </label>
      <div className="inline-flex items-center gap-1">
        <span className="text-slate-500">階層:</span>
        <select
          className="input !py-0.5 !text-xs"
          value={filter.depth}
          onChange={(e) =>
            onChange({ ...filter, depth: e.target.value as DepthMode })
          }
        >
          {DEPTHS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </div>
      {hasActive ? (
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
          onClick={onClear}
        >
          <X className="h-3 w-3" />
          フィルタをクリア
        </button>
      ) : null}
    </div>
  );
}

function toggle<T>(list: readonly T[], value: T): T[] {
  return list.includes(value)
    ? list.filter((v) => v !== value)
    : [...list, value];
}

function ChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  selected: readonly string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="mr-1 text-slate-500">{label}:</span>
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors',
              active
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
